import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  Pencil,
  CheckCircle,
  Archive,
  CreditCard,
  FileText,
  FileType,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useBillMutations, useBillStatusMutations, useBillDownload } from '../hooks';
import { CreatePaymentDialog } from './CreatePaymentDialog';
import type { Bill } from '../types';

interface BillActionsCellProps {
  bill: Bill;
  onEdit?: (bill: Bill) => void;
}

// Bill row actions. Status-shifting actions (Accept / Archive / Make payment)
// render as inline buttons gated by the bill's lifecycle state; the utilities
// (Edit, Download, Delete) live in the dropdown.
// Lifecycle: DRAFT → UNPAID → PAID (ARCHIVED is an off-ramp from DRAFT). A bill
// is editable in every state — editing never changes the state.
export function BillActionsCell({ bill, onEdit }: BillActionsCellProps) {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const { deleteMutation } = useBillMutations();
  const { acceptMutation, archiveMutation } = useBillStatusMutations();
  const downloadMutation = useBillDownload();

  const [paymentOpen, setPaymentOpen] = React.useState(false);

  const { id, state } = bill;

  const canEdit = hasAccess('bill', 'update');
  // PENDING is retired but legacy rows may still exist, so they keep their way
  // out until the status-convert script has run.
  const canAccept =
    (state === 'DRAFT' || state === 'PENDING') && hasAccess('bill', 'activate');
  const canArchive =
    (state === 'DRAFT' || state === 'PENDING') && hasAccess('bill', 'reject');
  const canCreatePayment = state === 'UNPAID' && hasAccess('payment', 'create');
  const canDownload = hasAccess('bill', 'download');
  // A paid bill is never deletable. The server additionally refuses a bill that
  // has payments against it — the list response carries no payments, so that
  // one surfaces as a server error rather than a hidden button.
  const canDelete = state !== 'PAID' && hasAccess('bill', 'delete');

  const busy = acceptMutation.isPending || archiveMutation.isPending;

  return (
    <div className="flex items-center gap-1">
      {canAccept && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => acceptMutation.mutate(id)}
          disabled={busy}
          className="gap-1.5 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
        >
          <CheckCircle size={15} />
          {t('activateBill')}
        </Button>
      )}

      {canArchive && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => archiveMutation.mutate(id)}
          disabled={busy}
          className="gap-1.5 text-slate-600 hover:bg-slate-100"
        >
          <Archive size={15} />
          {t('archiveBill')}
        </Button>
      )}

      {canCreatePayment && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setPaymentOpen(true)}
          className="gap-1.5 text-green-700 border-green-300 hover:bg-green-50"
        >
          <CreditCard size={15} />
          {t('makePayment')}
        </Button>
      )}

      {(canEdit || canDownload || canDelete) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm">
              <ChevronDown size={18} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[190px]">
            {canEdit && (
              <DropdownMenuItem
                onClick={() => onEdit?.(bill)}
                className="cursor-pointer"
              >
                <Pencil className="mr-2 h-4 w-4 text-slate-500" />
                <span>{t('edit')}</span>
              </DropdownMenuItem>
            )}

            {canDownload && (
              <>
                <DropdownMenuItem
                  onClick={() => downloadMutation.mutate({ id, format: 'PDF' })}
                  disabled={downloadMutation.isPending}
                  className="cursor-pointer"
                >
                  <FileText className="mr-2 h-4 w-4 text-slate-500" />
                  <span>{t('downloadPdf')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => downloadMutation.mutate({ id, format: 'Doc' })}
                  disabled={downloadMutation.isPending}
                  className="cursor-pointer"
                >
                  <FileType className="mr-2 h-4 w-4 text-slate-500" />
                  <span>{t('downloadDoc')}</span>
                </DropdownMenuItem>
              </>
            )}
            {canDelete && (
              <DropdownMenuItem
                onClick={() => deleteMutation.mutate(id)}
                disabled={deleteMutation.isPending}
                className={cn(
                  'cursor-pointer',
                  'text-red-600 dark:text-red-500',
                  'focus:text-red-700 focus:bg-red-50 dark:focus:bg-red-950/50',
                )}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                <span className="font-medium">{t('delete')}</span>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {canCreatePayment && (
        <CreatePaymentDialog
          billId={id}
          currency={bill.currency}
          maxAmount={bill.total - bill.paid}
          open={paymentOpen}
          onOpenChange={setPaymentOpen}
          hideTrigger
        />
      )}
    </div>
  );
}
